// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Answers: is my suburb being gouged? A leaderboard, plus the spread WITHIN
// each suburb — because the gap between neighbouring stations is usually bigger
// than the gap between suburbs.

import { loadSuburbs, prefs } from '../data';
import type { Suburb } from '../types';
import { cents, esc, escTip, priceColour, tankSaving } from '../format';
import { glossaryTerm } from '../glossary';
import { openOverlay } from '../components/drawer';

type SortKey = 'med' | 'spread' | 'sites' | 'name';

export async function renderSuburbs(root: HTMLElement): Promise<void> {
  root.innerHTML = '<div class="skeleton"></div>';
  const { suburbs } = await loadSuburbs();

  let state = prefs.get('suburbState', 'ALL');
  let sort: SortKey = (prefs.get('suburbSort', 'med') as SortKey);
  let asc = prefs.get('suburbAsc', '1') === '1';
  let query = '';

  root.innerHTML = `
    <div class="view-head">
      <h1>Cheapest and dearest suburbs</h1>
      <p>The middle unleaded price in each suburb over the last four months, and the
      ${glossaryTerm('spread')} between its own cheapest and dearest station. Suburbs with at least two stations.</p>
    </div>
    <div id="sub-cards"></div>
    <div class="panel">
      <div class="controls">
        <div class="seg" id="sub-state" role="group" aria-label="State">
          <button type="button" data-state="ALL">All</button>
          <button type="button" data-state="WA">WA</button>
          <button type="button" data-state="QLD">QLD</button>
        </div>
        <input type="search" id="sub-search" placeholder="Suburb…" aria-label="Search suburb" />
      </div>
      <div id="sub-table"></div>
    </div>
  `;

  const seg = root.querySelector('#sub-state') as HTMLElement;
  const syncSeg = () => seg.querySelectorAll('button').forEach((b) => {
    b.setAttribute('aria-pressed', String((b as HTMLElement).dataset.state === state));
  });
  seg.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('button') as HTMLElement | null;
    if (!btn) return;
    state = btn.dataset.state ?? 'ALL';
    prefs.set('suburbState', state);
    syncSeg();
    draw();
  });
  let timer = 0;
  (root.querySelector('#sub-search') as HTMLInputElement).addEventListener('input', (e) => {
    window.clearTimeout(timer);
    const v = (e.target as HTMLInputElement).value;
    timer = window.setTimeout(() => { query = v.trim().toLowerCase(); draw(); }, 300);
  });
  syncSeg();

  function draw(): void {
    const pool = suburbs.filter((s) => state === 'ALL' || s.st === state);
    const shown = query ? pool.filter((s) => s.s.toLowerCase().includes(query) || s.pc.includes(query)) : pool;
    renderCards(root.querySelector('#sub-cards')!, pool);
    renderTable(root.querySelector('#sub-table')!, shown, sort, asc, (key) => {
      if (key === sort) asc = !asc; else { sort = key; asc = key === 'med' || key === 'name'; }
      prefs.set('suburbSort', sort);
      prefs.set('suburbAsc', asc ? '1' : '0');
      draw();
    });
  }
  draw();
}

function renderCards(host: HTMLElement, pool: Suburb[]): void {
  if (!pool.length) { host.innerHTML = ''; return; }
  const byMed = [...pool].sort((a, b) => a.med - b.med);
  const bySpread = [...pool].sort((a, b) => (b.max - b.min) - (a.max - a.min));
  const cheapest = byMed[0];
  const dearest = byMed[byMed.length - 1];
  const widest = bySpread[0];
  const medianSpread = bySpread[Math.floor(bySpread.length / 2)];
  host.innerHTML = `
    <div class="grid-cards" style="margin-bottom:var(--space-lg)">
      <div class="stat-card"><b>${cents(cheapest.med)}c</b><span class="truncate">cheapest suburb — ${esc(cheapest.s)}</span></div>
      <div class="stat-card"><b>${cents(dearest.med)}c</b><span class="truncate">dearest suburb — ${esc(dearest.s)}</span></div>
      <div class="stat-card"><b>${cents(medianSpread.max - medianSpread.min)}c</b><span>typical spread inside one suburb</span></div>
      <div class="stat-card"><b>${cents(widest.max - widest.min)}c</b><span class="truncate">widest — ${esc(widest.s)}</span></div>
    </div>
    <p class="brand-sub" style="margin:-8px 0 var(--space-lg)">
      Worth noticing: the typical gap between the cheapest and dearest station <em>inside a single suburb</em>
      (${cents(medianSpread.max - medianSpread.min)}c, ${tankSaving(medianSpread.max - medianSpread.min)} a tank)
      is often larger than the gap between one suburb and the next. Which street you stop on matters more than which suburb.
    </p>
  `;
}

function renderTable(host: HTMLElement, rows: Suburb[], sort: SortKey, asc: boolean, onSort: (k: SortKey) => void): void {
  if (!rows.length) { host.innerHTML = '<p class="empty">No suburbs match that search.</p>'; return; }
  const dir = asc ? 1 : -1;
  const sorted = [...rows].sort((a, b) => {
    if (sort === 'name') return dir * a.s.localeCompare(b.s);
    if (sort === 'spread') return dir * ((a.max - a.min) - (b.max - b.min));
    if (sort === 'sites') return dir * (a.sites - b.sites);
    return dir * (a.med - b.med);
  });
  const meds = rows.map((r) => r.med).sort((a, b) => a - b);
  const lo = meds[Math.floor(meds.length * 0.05)];
  const hi = meds[Math.floor(meds.length * 0.95)];
  const maxSpread = Math.max(...rows.map((r) => r.max - r.min), 1);
  const arrow = (k: SortKey) => (sort === k ? (asc ? ' ▲' : ' ▼') : '');

  host.innerHTML = `
    <div class="table-scroll">
      <table>
        <thead><tr>
          <th class="n">#</th>
          <th class="sortable" data-sort="name">Suburb${arrow('name')}</th>
          <th>Region</th>
          <th class="n sortable" data-sort="sites">Stations${arrow('sites')}</th>
          <th class="n sortable" data-sort="med">Middle price${arrow('med')}</th>
          <th class="n sortable" data-sort="spread">Spread${arrow('spread')}</th>
          <th style="min-width:130px">Cheapest → dearest</th>
        </tr></thead>
        <tbody>${sorted.slice(0, 400).map((s, i) => {
          const spread = s.max - s.min;
          return `<tr class="clickable" data-key="${esc(`${s.s}|${s.st}`)}">
            <td class="n rank">${i + 1}</td>
            <td>${esc(s.s)} <small style="color:var(--text-tertiary)">${esc(s.pc)}</small></td>
            <td><span class="truncate" style="display:block;max-width:18ch">${esc(s.r)}</span></td>
            <td class="n">${s.sites}</td>
            <td class="n"><span style="color:${priceColour(s.med, lo, hi)};font-weight:650"
              data-tip="${escTip(`${s.s}\nmiddle price ${cents(s.med)}c\nmiddle half of stations ${cents(s.p25)}–${cents(s.p75)}c`)}">${cents(s.med)}</span></td>
            <td class="n" data-tip="${escTip(`${cents(spread)}c between the cheapest and dearest station\n${tankSaving(spread)} on a 55-litre tank`)}">${cents(spread)}</td>
            <td>
              <span style="display:block;height:9px;border-radius:5px;background:var(--bg-elevated);position:relative;min-width:110px"
                data-tip="${escTip(`${cents(s.min)}c at the cheapest station\n${cents(s.max)}c at the dearest`)}">
                <span style="display:block;position:absolute;left:0;top:0;height:9px;border-radius:5px;background:linear-gradient(90deg,#167a4a,#c2410c);width:${Math.max(4, (spread / maxSpread) * 100).toFixed(1)}%"></span>
              </span>
            </td>
          </tr>`;
        }).join('')}</tbody>
      </table>
    </div>
    <p class="brand-sub" style="margin-top:var(--space-sm)">Showing ${Math.min(400, sorted.length)} of ${sorted.length} suburbs. Click a column heading to sort, or a row for detail.</p>
  `;
  host.querySelectorAll('th.sortable').forEach((th) => {
    th.addEventListener('click', () => onSort((th as HTMLElement).dataset.sort as SortKey));
  });
  host.querySelectorAll('tr.clickable').forEach((tr) => {
    tr.addEventListener('click', () => {
      const key = (tr as HTMLElement).dataset.key ?? '';
      const s = rows.find((x) => `${x.s}|${x.st}` === key);
      if (!s) return;
      const spread = s.max - s.min;
      openOverlay('drawer', `
        <h2>${esc(s.s)}</h2>
        <p class="sub">${esc(s.pc)} · ${esc(s.r)} (${esc(s.st)})</p>
        <div class="grid-cards" style="margin-top:var(--space-lg)">
          <div class="stat-card"><b>${cents(s.med)}c</b><span>middle price</span></div>
          <div class="stat-card"><b>${cents(s.min)}c</b><span>cheapest station seen</span></div>
          <div class="stat-card"><b>${cents(s.max)}c</b><span>dearest station seen</span></div>
          <div class="stat-card"><b>${tankSaving(spread)}</b><span>a tank, cheapest vs dearest</span></div>
        </div>
        <p style="margin-top:var(--space-lg);color:var(--text-secondary)">
          ${s.sites} station${s.sites === 1 ? '' : 's'} reporting, ${s.n.toLocaleString()} price readings over the last four months.
          The middle half of readings fell between ${cents(s.p25)}c and ${cents(s.p75)}c.
        </p>
        <p class="brand-sub" style="margin-top:var(--space-md)">
          These are unleaded 91 readings across the whole window, so the cheapest and dearest figures include
          different points in the price cycle as well as different stations.
        </p>
      `, s.s);
    });
  });
}
